import { Injectable, Signal, computed, inject } from '@angular/core';

import { TREND_COVERAGE } from '../data/dashboard.seed';
import {
  addDays,
  bucketEndDate,
  parseIsoDate,
  startOfDay,
  toIsoDate,
} from '../functions/date.functions';
import type {
  DashboardChartGranularity,
  DateRange,
  DateRangePreference,
  DateRangePresetId,
} from '../types';
import { UserPreferencesService } from './user-preferences.service';

export interface DateRangePresetMeta {
  readonly id: DateRangePresetId;
  readonly label: string;
}

const PRESETS: ReadonlyArray<DateRangePresetMeta> = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 Days' },
  { id: 'last30', label: 'Last 30 Days' },
  { id: 'last90', label: 'Last 90 Days' },
  { id: 'thisMonth', label: 'This Month' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: 'thisYear', label: 'This Year' },
  { id: 'lastYear', label: 'Last Year' },
  { id: 'last365', label: 'Last 365 Days' },
  { id: 'custom', label: 'Custom Range' },
];

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

@Injectable({ providedIn: 'root' })
export class DateFilterService {
  private readonly prefs = inject(UserPreferencesService);
  private readonly preference: Signal<DateRangePreference> = this.prefs.select('dateRange');

  readonly presets = PRESETS;

  readonly preset = computed<DateRangePresetId>(() => this.preference().presetId);

  readonly range = computed<DateRange>(() => {
    const pref = this.preference();
    if (pref.presetId === 'custom') {
      const from = parseIsoDate(pref.fromIso);
      const to = parseIsoDate(pref.toIso);
      if (from && to) return { from: startOfDay(from), to: startOfDay(to) };
    }
    return computePresetRange(pref.presetId);
  });

  readonly durationDays = computed<number>(() => {
    const { from, to } = this.range();
    const ms = startOfDay(to).getTime() - startOfDay(from).getTime();
    return Math.max(1, Math.round(ms / 86_400_000) + 1);
  });

  readonly allowedGranularities = computed<ReadonlyArray<DashboardChartGranularity>>(() => {
    const days = this.durationDays();
    if (days <= 1) return ['day'];
    if (days <= 7) return ['day'];
    if (days <= 31) return ['day', 'week'];
    if (days <= 180) return ['day', 'week', 'month'];
    if (days <= 365) return ['week', 'month', 'quarter'];
    return ['month', 'quarter', 'year'];
  });

  readonly recommendedGranularity = computed<DashboardChartGranularity>(() => {
    const days = this.durationDays();
    if (days <= 60) return 'day';
    if (days <= 250) return 'week';
    if (days <= 365) return 'month';
    if (days <= 730) return 'quarter';
    return 'year';
  });

  readonly isGranularityLocked = computed<boolean>(() => this.allowedGranularities().length === 1);

  hasTrendDataAtGranularity(granularity: DashboardChartGranularity): boolean {
    const coverage = TREND_COVERAGE[granularity];
    const covFrom = parseIsoDate(coverage.fromIso);
    const covStart = parseIsoDate(coverage.toIso);
    if (!covFrom || !covStart) return false;
    const covTo = bucketEndDate(covStart, granularity);
    const { from, to } = this.range();
    return covTo.getTime() >= from.getTime() && covFrom.getTime() <= to.getTime();
  }

  readonly hasTrendDataAtRecommended = computed<boolean>(() =>
    this.hasTrendDataAtGranularity(this.recommendedGranularity()),
  );

  readonly displayLabel = computed<string>(() => {
    const { from, to } = this.range();
    if (from.getTime() === to.getTime()) return formatDisplay(from);
    return `${formatDisplay(from)} - ${formatDisplay(to)}`;
  });

  selectPreset(id: DateRangePresetId): void {
    if (id === 'custom') {
      const current = this.range();
      this.prefs.set('dateRange', {
        presetId: 'custom',
        fromIso: toIsoDate(current.from),
        toIso: toIsoDate(current.to),
      });
      return;
    }
    this.prefs.set('dateRange', { presetId: id, fromIso: '', toIso: '' });
  }

  setCustomRange(from: Date, to: Date): void {
    const f = startOfDay(from);
    const t = startOfDay(to);
    const [lo, hi] = f.getTime() <= t.getTime() ? [f, t] : [t, f];
    this.prefs.set('dateRange', {
      presetId: 'custom',
      fromIso: toIsoDate(lo),
      toIso: toIsoDate(hi),
    });
  }

  resolveGranularity(
    requested: DashboardChartGranularity | null | undefined,
  ): DashboardChartGranularity {
    const allowed = this.allowedGranularities();
    const recommended = this.recommendedGranularity();
    if (!requested || !allowed.includes(requested)) return recommended;
    const order: ReadonlyArray<DashboardChartGranularity> = [
      'day',
      'week',
      'month',
      'quarter',
      'year',
    ];
    const reqIdx = order.indexOf(requested);
    const recIdx = order.indexOf(recommended);
    if (recIdx - reqIdx >= 2) return recommended;
    return requested;
  }
}

function formatDisplay(date: Date): string {
  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`;
}

function computePresetRange(id: DateRangePresetId): DateRange {
  const today = startOfDay(new Date());
  switch (id) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { from: y, to: y };
    }
    case 'last7':
      return { from: addDays(today, -6), to: today };
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'last90':
      return { from: addDays(today, -89), to: today };
    case 'last365':
      return { from: addDays(today, -364), to: today };
    case 'thisMonth':
      return {
        from: new Date(today.getFullYear(), today.getMonth(), 1),
        to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      };
    case 'lastMonth':
      return {
        from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        to: new Date(today.getFullYear(), today.getMonth(), 0),
      };
    case 'thisYear':
      return {
        from: new Date(today.getFullYear(), 0, 1),
        to: new Date(today.getFullYear(), 11, 31),
      };
    case 'lastYear':
      return {
        from: new Date(today.getFullYear() - 1, 0, 1),
        to: new Date(today.getFullYear() - 1, 11, 31),
      };
    case 'custom':
    default:
      return { from: today, to: today };
  }
}
